import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();

    // Supa Kiba: only play while the video is on screen, the tab is visible
    // and the mobile menu is closed. Saves memory and battery on phones.
    this._skVisible = true;
    this._skUpdate = () => {
      const menuOpen = !!document.querySelector('header-drawer details[open]');
      if (this._skVisible && !document.hidden && !menuOpen) {
        const playing = videoElement.play();
        if (playing && typeof playing.catch === 'function') playing.catch(() => {});
      } else {
        videoElement.pause();
      }
    };

    if ('IntersectionObserver' in window) {
      this._skObserver = new IntersectionObserver((entries) => {
        this._skVisible = entries[0] ? entries[0].isIntersecting : true;
        this._skUpdate();
      });
      this._skObserver.observe(this);
    }

    document.addEventListener('visibilitychange', this._skUpdate);

    const menuToggle = document.querySelector('header-drawer details');
    if (menuToggle) {
      this._skMenuObserver = new MutationObserver(this._skUpdate);
      this._skMenuObserver.observe(menuToggle, { attributes: true, attributeFilter: ['open'] });
    }
  }

  disconnectedCallback() {
    if (super.disconnectedCallback) super.disconnectedCallback();
    if (this._skObserver) this._skObserver.disconnect();
    if (this._skMenuObserver) this._skMenuObserver.disconnect();
    if (this._skUpdate) document.removeEventListener('visibilitychange', this._skUpdate);
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
